export interface PaginationMeta {
  total: number;
  page: number;
  perPage: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginationMeta;
}

export const normalizePaginationMeta = (
  meta?: Record<string, unknown> | null,
): PaginationMeta | undefined => {
  if (!meta || typeof meta !== 'object') return undefined

  const toNumber = (value: unknown, fallback: number): number => {
    if (value === null || value === undefined || value === '') return fallback
    const num = typeof value === 'number' ? value : Number(value)
    return Number.isFinite(num) ? num : fallback
  }

  const bag = meta as Record<string, unknown>
  const total = toNumber(bag.total ?? bag.totalCount ?? bag.count, 0)
  const page = toNumber(bag.page ?? bag.currentPage ?? bag.current_page, 1)
  const perPage = toNumber(
    bag.perPage ?? bag.per_page ?? bag.limit ?? bag.perpage ?? bag.per ?? bag.pageSize,
    25,
  )

  if (total < 0 || perPage <= 0 || page <= 0) {
    return {
      total: Math.max(total, 0),
      page: Math.max(page, 1),
      perPage: Math.max(perPage, 1),
    }
  }

  return { total, page, perPage }
}

/**
 * Helper to automatically fetch all paginated results from an API endpoint.
 *
 * @param fetchPage - A function that accepts (page, perPage) and returns a paginated response.
 * @param perPage - Number of items per page (default: 25)
 * @returns All merged results and combined meta info
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number, perPage: number) => Promise<PaginatedResponse<T>>,
  perPage: number = 25,
): Promise<PaginatedResponse<T>> {
  let allData: T[] = [];
  let currentPage = 1;
  let meta: PaginationMeta = { total: 0, page: 1, perPage };

  while (true) {
    const response = await fetchPage(currentPage, perPage);
    if (!response?.data?.length) break;

    allData = allData.concat(response.data);
    meta = response.meta;

    const totalPages = Math.ceil(response.meta.total / response.meta.perPage);
    if (currentPage >= totalPages) break;

    currentPage++;
  }

  return { data: allData, meta };
}
